import React, { useEffect, useRef } from "react";
import useSearch from "redux/hooks/useSearch";
import useRouter from "hooks/useRouter";
import useDomain from "hooks/useDomain";

import Ad from "components/Ad";
import ArticleResults from "components/ArticleResults";
import BrandResults from "components/BrandResults";
import DispensaryResults from "components/DispensaryResults";
import Head from "next/head";
import MenuItemResults from "components/MenuItemResults";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";
import SearchFilter from "components/SearchFilter";
import StrainResults from "components/StrainResults";

import { NextPage, NextPageContext } from "next";
import { getSearchState } from "redux/reducers/search";
import { getDealsState } from "redux/reducers/deals";
import { getCountryCode, getUserLocation } from "redux/selectors/location";
import classnames from "classnames";

const SearchPage: NextPage = () => {
  const { searchTerm, categoryRelevanceOrder, getSearchResults } = useSearch();
  const { asPath } = useRouter();
  const baseUrl = useDomain();

  const {
    parsedQuery: { searchCategory },
  } = useRouter();

  // Get new search results when search term changes
  const isMounted = useRef(false);
  useEffect(() => {
    if (isMounted.current) {
      getSearchResults();
    } else {
      isMounted.current = true;
    }
  }, [searchTerm]);

  const resultingCategories = searchCategory
    ? [searchCategory]
    : categoryRelevanceOrder;

  const getCategoryComponent = (category: string) => {
    switch (category) {
      case "article":
        return <ArticleResults />;
      case "brand":
        return <BrandResults />;
      case "dispensary":
        return <DispensaryResults />;
      case "menuItem":
        return <MenuItemResults />;
      case "strain":
        return <StrainResults />;

      default:
        return <></>;
    }
  };

  return (
    <>
      <Head>
        <meta property="og:url" content={`${baseUrl}${asPath}`} key="og:url" />
        <title>Search | Leafly</title>
        <meta name="robots" content="noindex,noarchive,nofollow" />
      </Head>
      <section className="bg-white border-light-grey">
        <div className="container pt-lg">
          {searchTerm && (
            <h1 className="heading--m mb-xl mt-sm">{`Search results for "${searchTerm}"`}</h1>
          )}
          <SearchFilter sortedCategories={categoryRelevanceOrder} />
        </div>
      </section>

      {resultingCategories.map((category: string, index: number) => (
        <section
          key={category}
          className={classnames("pb-section results-section", {
            "pt-xl": index === 0,
            "pt-section": index > 0,
          })}
        >
          <div className="container">
            <div className="row">
              <div className="col lg:col-3/4">
                {getCategoryComponent(category)}
              </div>

              {index === 0 && (
                <div className="hidden lg:block lg:col-1/4 pt-section">
                  <div className="relative w-full">
                    <div className="absolute right-0">
                      <Ad tag="upper" orientation="vertical" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      ))}

      <section className="container py-section">
        <RecentlyViewedMenuItems className="mb-section" />
        <Ad tag="lower" />
      </section>

      <style jsx>
        {`
          .results-section {
            background: linear-gradient(
              180deg,
              var(--color-leafly-white) 0%,
              var(--color-white) 100%
            );
          }
        `}
      </style>
    </>
  );
};

SearchPage.getInitialProps = async (ctx: NextPageContext) => {
  const currentState = ctx.store.getState();
  const coordinates = getUserLocation(currentState);
  const countryCode = getCountryCode(currentState);

  await Promise.all([
    getSearchState(ctx, coordinates, countryCode),
    getDealsState(ctx, coordinates, countryCode),
  ]);
};

export default SearchPage;
